import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from '@grebban/style-system-react';

const IndicatorCounter = styled('div')`
    width: 100%;
    display: flex;
    gap: 24px;
    align-items: center;
`;

const Indicator = styled('div')`
    height: 2px;
    width: 100%;
    border-radius: 8px;
    backdrop-filter: blur(12px);
    background-color: var(--color-transparency-white-30);
    & > div {
        width: ${({ progress }) => progress}%;
        transition: width 0.1s linear;
    }
`;

const Step = styled('div')`
    height: 100%;
    border-radius: 8px;
    background-color: var(--color-base-white);
`;

interface ProgressIndicatorProps {
    activeIndex: number;
    totalSlides: number;
    isPaused: boolean;
    delay: number; // in milliseconds
    onDelayComplete: (index: number) => void;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
    activeIndex,
    totalSlides,
    isPaused,
    delay,
    onDelayComplete,
}) => {
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef<number | null>(null);

    const handleSlideComplete = useCallback(
        (index: number) => {
            onDelayComplete(index);
        },
        [onDelayComplete],
    );

    useEffect(() => {
        const updateProgress = () => {
            if (!isPaused) {
                setProgress((prevProgress) => {
                    const newProgress = prevProgress + 100 / (delay / 10);
                    if (newProgress >= 100) {
                        handleSlideComplete((activeIndex + 1) % totalSlides);
                        return 0;
                    }
                    return newProgress;
                });
            }
        };

        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = window.setInterval(updateProgress, 10);

        return () => {
            if (intervalRef.current !== null) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPaused, activeIndex, handleSlideComplete, totalSlides, delay]);

    useEffect(() => {
        setProgress(0);
    }, [activeIndex]);

    return (
        <IndicatorCounter>
            {Array.from({ length: totalSlides }).map((_, index) => (
                <Indicator key={`slide-step-${index}`} progress={activeIndex === index ? progress : 0}>
                    <Step />
                </Indicator>
            ))}
        </IndicatorCounter>
    );
};

export default ProgressIndicator;
